import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import UpdateIcon from "@mui/icons-material/Update";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import api, { TOKEN_STORAGE_KEY } from "../api/client";
import { AuthContext } from "../context/AuthContext";
import useLiveNoticeRefresh from "../hooks/useLiveNoticeRefresh";
import { noticeReadOptions } from "../services/systemNoticeEvents";


const typeConfig = {
  update: { icon: <UpdateIcon />, color: "primary", label: "Update" },
  patch: { icon: <NewReleasesIcon />, color: "success", label: "Patch Notes" },
  alert: { icon: <WarningIcon />, color: "warning", label: "Alert" },
  info: { icon: <InfoIcon />, color: "info", label: "Information" },
};

export default function AnnouncementModal() {
  const { user } = useContext(AuthContext);
  const [pending, setPending] = useState({ owner: null, items: [] });
  const [currentId, setCurrentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const acknowledgment = useRef(null);
  useEffect(() => {
    setSaving(false); setError('');
    return () => acknowledgment.current?.abort();
  }, [user?.id]);
  const readPending = useCallback(async signal => {
    const response = await api.get('/announcements/pending', noticeReadOptions(signal));
    if (signal.aborted || !Array.isArray(response.data)) return;
    const items = response.data;
    setPending({ owner: user?.id, items });
    setCurrentId(previous => items.some(item => item.id === previous) ? previous : items[0]?.id ?? null);
  }, [user?.id]);
  const refresh = useLiveNoticeRefresh(readPending, !!user?.id);
  const announcement = pending.owner === user?.id ? pending.items.find(item => item.id === currentId) : null;
  const handleDismiss = async () => {
    if (!announcement || saving) return;
    const controller = new AbortController(); acknowledgment.current = controller;
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    setSaving(true); setError('');
    try {
      await api.post(`/announcements/${announcement.id}/acknowledge`, {}, { signal: controller.signal, timeout: 10000, expectedSessionToken: token });
      if (controller.signal.aborted || localStorage.getItem(TOKEN_STORAGE_KEY) !== token) return;
      setPending(previous => ({ ...previous, items: previous.items.filter(item => item.id !== announcement.id) }));
      await refresh();
    } catch {
      if (!controller.signal.aborted) setError('Unable to save your acknowledgment. Please try again.');
    } finally { if (!controller.signal.aborted) setSaving(false); }
  };
  if (!announcement) return null;

  const config = typeConfig[announcement.type] || typeConfig.info;
  const formattedDate = new Date(announcement.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Dialog
      open
      aria-labelledby="patch-note-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)"
              : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
          color: "white",
          p: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={2} alignItems="center">
            {config.icon}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
                {announcement.version && (
                  <Chip
                    label={`v${announcement.version}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "white",
                    }}
                  />
                )}
              </Stack>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {formattedDate}
              </Typography>
            </Box>
          </Stack>
          <IconButton aria-label="Acknowledge and close update" disabled={saving} size="small" onClick={handleDismiss} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogTitle id="patch-note-title" sx={{ pb: 1 }}>
        <Typography component="span" variant="h5" sx={{ fontWeight: 700 }}>
          {announcement.title}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.8,
          }}
        >
          {announcement.content}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleDismiss} disabled={saving} variant="contained" fullWidth>
          {saving ? "Saving acknowledgment..." : "Got it!"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
